// contexts/ModalOperationsContext.tsx
/**
 * ModalOperationsContext - Centralizes modal trigger handlers
 *
 * Eliminates prop drilling of modal-opening handlers through
 * CoursePlannerColumn -> CoursePlanner -> SemesterGrid.
 *
 * Usage:
 *   // Provider (in CoursePlannerColumn):
 *   <ModalOperationsProvider value={{ onOpenAddCourse, ... }}>
 *
 *   // Consumer (in SemesterGrid, CoursePlanner):
 *   const { onOpenAddCourse, onOpenAPCredits, ... } = useModalOperations();
 */

import { createContext, useContext, type ReactNode } from 'react';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Shape passed through ModalOperationsContext. Bundles modal-trigger
 * callbacks from useAppModals plus the inline grade-update closure
 * defined in App.js.
 */
export interface ModalOperationsContextValue {
  onOpenAddCourse: (semesterCode: string, semesterName: string) => void;
  onOpenAPCredits: () => void;
  onAddOtherCredit: () => void;
  onManageSemesters: () => void;
  onUpdateGrade: (semester: string, courseCode: string, grade: string | null) => Promise<void>;
}

interface ModalOperationsProviderProps {
  value: ModalOperationsContextValue;
  children: ReactNode;
}

const ModalOperationsContext = createContext<ModalOperationsContextValue | null>(null);
ModalOperationsContext.displayName = 'ModalOperationsContext';

export const useModalOperations = (): ModalOperationsContextValue => {
  const context = useContext(ModalOperationsContext);
  if (!context) {
    throw new Error('useModalOperations must be used within a ModalOperationsProvider');
  }
  return context;
};

export const ModalOperationsProvider = ({ value, children }: ModalOperationsProviderProps) => {
  return (
    <ModalOperationsContext.Provider value={value}>
      {children}
    </ModalOperationsContext.Provider>
  );
};

export default ModalOperationsContext;
